<script setup lang="ts">
  import * as z from 'zod';
  import { computed } from 'vue';
  import { usePropertyForm } from '../../../model/usePropertyForm';
  import { useTableProperties } from '../../../model/useTableProperties';
  import type { TableProperties as TablePropsType } from '../../../model/form.types';
  import { FORM_FIELD_CHARACTER_LIMITS } from '../../../lib/form-field-character-limits';
  import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Input,
    IconImage,
    Label,
    Switch,
  } from '@/shared/ui';
  import ConfigsIcon from '../../../../../shared/assets/icons/ConfigsIcon.svg';

  interface Props {
    elementId: string;
  }

  const props = defineProps<Props>();

  const { element, updateProperties } = useTableProperties(props.elementId);

  const isHeaderTable = computed(() => element.value?.properties.isHeader === true);
  // The stored value is the whole truth: a missing is_multiple is OFF. Defaulting it on is the
  // legacy allowAddRows defect, where every table looked multiple without anyone choosing it.
  const isMultiple = computed(() => element.value?.properties.is_multiple === true);
  const multipleSwitchId = computed(() => `table-multiple-${props.elementId}`);

  function handleMultipleChange(value: boolean): void {
    updateProperties({ is_multiple: value });
  }

  usePropertyForm<{ name: string }, TablePropsType>({
    elementId: () => props.elementId,
    validationSchema: z.object({
      name: z.string().min(1, 'O nome é obrigatório').max(FORM_FIELD_CHARACTER_LIMITS.FIELD_ID),
    }),
    extractInitialValues: (properties) => ({
      name: properties?.name || '',
    }),
    mapValuesToProperties: (values, currentProperties) => ({
      properties: {
        ...currentProperties,
        name: values.name || currentProperties.name,
      },
    }),
  });
</script>

<template>
  <div class="border-b border-gray-02 pb-4">
    <div class="flex items-center gap-3 border-b border-gray-03 pb-3 mb-4">
      <div class="bg-gray-03 flex justify-center items-center w-6 h-6 rounded-sm">
        <IconImage
          class="text-white-01 inline-flex items-center justify-center leading-none [&_svg]:block [&_svg]:h-5 [&_svg]:w-5 [&_path]:fill-current"
          :src="ConfigsIcon"
          alt="Configurações"
        />
      </div>
      <h3 class="text-sm font-normal text-[var(--color-gray-07)]">
        Configurações
      </h3>
    </div>

    <FormField
      v-slot="{ componentField }"
      name="name"
    >
      <FormItem>
        <FormLabel class="font-normal">
          ID do Campo
        </FormLabel>
        <FormControl>
          <Input
            v-bind="componentField"
            placeholder="nome_da_tabela"
            class="h-11"
            :maxlength="FORM_FIELD_CHARACTER_LIMITS.FIELD_ID"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <div
      v-if="!isHeaderTable"
      class="mt-4 flex items-center justify-between"
    >
      <Label
        :for="multipleSwitchId"
        class="text-sm font-normal text-[var(--color-gray-07)]"
      >Tabela múltipla</Label>
      <div class="flex items-center gap-2">
        <Switch
          :id="multipleSwitchId"
          :model-value="isMultiple"
          aria-label="Tabela múltipla"
          data-testid="multiple-table-toggle"
          @update:model-value="handleMultipleChange"
        />
      </div>
    </div>
  </div>
</template>
